import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { env } from '@/config/env'
import {
  fetchPublicTenantBranding,
  tenantBrandingString,
  type TenantBrandingRecord,
} from '@/services/api/tenant-branding'
import {
  acknowledgeCpAddonCodesInStorage,
  computePendingCpAddonCelebrationCodes,
  purchasableAddonCodesFromCpEntitlements,
} from '@/utils/cp-addon-celebration-storage'

const BRANDING_CACHE_KEY_V1 = 'tb.tenantBranding.v1'

function tenantBrandingCacheStorageKey(): string {
  return `${BRANDING_CACHE_KEY_V1}:${env.apiUrl}`
}

const HEX_6 = /^#?([0-9a-f]{6})$/i
const HEX_3 = /^#?([0-9a-f]{3})$/i

function expandHex3(short: string): string {
  const [a, b, c] = short.split('')
  return `${a}${a}${b}${b}${c}${c}`
}

/**
 * Parses #RGB or #RRGGBB into [r, g, b] or null.
 */
function parseHexRgb(hex: string): [number, number, number] | null {
  const t = hex.trim()
  let m = HEX_6.exec(t)
  if (m) {
    const n = parseInt(m[1], 16)
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
  }
  m = HEX_3.exec(t)
  if (m) {
    const full = expandHex3(m[1])
    const n = parseInt(full, 16)
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
  }
  return null
}

function rgbTripletString(rgb: [number, number, number]): string {
  return `${rgb[0]} ${rgb[1]} ${rgb[2]}`
}

/**
 * Darkens RGB channels by mixing toward black (0–1 factor of remaining light).
 */
function shadeTowardBlack(rgb: [number, number, number], factor: number): [number, number, number] {
  const f = Math.max(0, Math.min(1, factor))
  return rgb.map((c) => Math.round(c * f)) as [number, number, number]
}

/**
 * Lightens RGB channels by mixing toward white.
 * factor = 0 → white, factor = 1 → original color
 */
function shadeTowardWhite(rgb: [number, number, number], factor: number): [number, number, number] {
  const f = Math.max(0, Math.min(1, factor))
  return rgb.map((c) => Math.round(c * f + 255 * (1 - f))) as [number, number, number]
}

function applyCssBrandFromPrimary(root: HTMLElement, primaryHex: string): [number, number, number] {
  const parsed = parseHexRgb(primaryHex)
  /* Default indigo aligns with subscription-dashboard static brand */
  const fallback: [number, number, number] = [99, 102, 241]
  const base = parsed ?? fallback
  root.style.setProperty('--tenant-brand-300', rgbTripletString(shadeTowardWhite(base, 0.55)))
  root.style.setProperty('--tenant-brand-400', rgbTripletString(shadeTowardWhite(base, 0.75)))
  root.style.setProperty('--tenant-brand-500', rgbTripletString(base))
  root.style.setProperty('--tenant-brand-600', rgbTripletString(shadeTowardBlack(base, 0.88)))
  root.style.setProperty('--tenant-brand-700', rgbTripletString(shadeTowardBlack(base, 0.75)))
  root.style.setProperty('--brand-primary', rgbTripletString(base))
  root.style.setProperty('--brand-primary-rgb', `${base[0]}, ${base[1]}, ${base[2]}`)
  return base
}

/**
 * Accent stripe / secondary UI (falls back to primary when accentColor unset or invalid).
 */
function applyAccentCss(root: HTMLElement, b: TenantBrandingRecord, primaryRgb: [number, number, number]): void {
  const accentHex = tenantBrandingString(b, 'accentColor').trim()
  const accentParsed = accentHex ? parseHexRgb(accentHex) : null
  const accentRgb = accentParsed ?? primaryRgb
  root.style.setProperty('--tenant-accent-500', rgbTripletString(accentRgb))
  root.style.setProperty('--tenant-accent-600', rgbTripletString(shadeTowardBlack(accentRgb, 0.88)))
}

function applyBrandingToDocument(b: TenantBrandingRecord): void {
  const root = document.documentElement
  const primary = tenantBrandingString(b, 'primaryColor').trim() || '#6366f1'
  const primaryRgb = applyCssBrandFromPrimary(root, primary)
  applyAccentCss(root, b, primaryRgb)

  // document.title is managed by router (syncDocumentTitle) so it can include the current page.

  const fav = tenantBrandingString(b, 'faviconUrl').trim()
  if (fav) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = fav
  }

  const sans = tenantBrandingString(b, 'fontFamilySans').trim()
  if (sans) {
    root.style.setProperty('--tenant-font-sans', sans)
  } else {
    root.style.removeProperty('--tenant-font-sans')
  }

  const heading = tenantBrandingString(b, 'fontFamilyHeading').trim()
  if (heading) {
    root.style.setProperty('--tenant-font-heading', heading)
  } else {
    root.style.removeProperty('--tenant-font-heading')
  }
}

export const useTenantBrandingStore = defineStore('tenantBranding', () => {
  const branding = ref<TenantBrandingRecord>({})
  const brandingRevision = ref(0)
  const cpEntitlements = ref<string[]>([])
  const seatingProActive = computed(
    () => cpEntitlements.value.includes('addon:seating_pro'),
  )
  const giftCardsActive = computed(
    () => cpEntitlements.value.includes('addon:gift_cards'),
  )
  const vendorSystemActive = computed(
    () => cpEntitlements.value.includes('addon:vendor_system'),
  )
  const analyticsActive = computed(
    () => cpEntitlements.value.includes('addon:analytics'),
  )
  const loaded = ref(false)
  /** Purchasable CP add-on codes (no `addon:` prefix) pending celebration UI after network branding fetch. */
  const pendingCpAddonCelebration = ref<string[]>([])

  function refreshPendingCpAddonCelebration(nextEnt: string[]): void {
    const purchasable = purchasableAddonCodesFromCpEntitlements(nextEnt)
    pendingCpAddonCelebration.value = computePendingCpAddonCelebrationCodes(purchasable)
  }

  function dismissCpAddonCelebration(): void {
    const codes = pendingCpAddonCelebration.value
    if (codes.length === 0) {
      return
    }
    acknowledgeCpAddonCodesInStorage(codes)
    pendingCpAddonCelebration.value = []
  }

  /** Coalesce concurrent load() calls (e.g. bootstrap + route guards). */
  let loadInflight: Promise<void> | null = null

  async function runLoad(): Promise<void> {
    try {
      const payload = await fetchPublicTenantBranding()
      const b = payload.branding
      const rev = payload.brandingRevision
      let nextEnt = Array.isArray(payload.cpEntitlements) ? payload.cpEntitlements.map(String) : []
      // Keep store entitlements aligned with API booleans if the array ever omits addon codes.
      if (payload.seatingProActive === true && !nextEnt.includes('addon:seating_pro')) {
        nextEnt = [...nextEnt, 'addon:seating_pro']
      }
      if (payload.giftCardsActive === true && !nextEnt.includes('addon:gift_cards')) {
        nextEnt = [...nextEnt, 'addon:gift_cards']
      }
      if (payload.vendorSystemActive === true && !nextEnt.includes('addon:vendor_system')) {
        nextEnt = [...nextEnt, 'addon:vendor_system']
      }
      if (payload.analyticsActive === true && !nextEnt.includes('addon:analytics')) {
        nextEnt = [...nextEnt, 'addon:analytics']
      }
      branding.value = b
      brandingRevision.value = rev
      cpEntitlements.value = nextEnt
      applyBrandingToDocument(b)
      refreshPendingCpAddonCelebration(nextEnt)
      try {
        localStorage.setItem(
          tenantBrandingCacheStorageKey(),
          JSON.stringify({
            branding: b,
            brandingRevision: rev,
            cpEntitlements: nextEnt,
            savedAt: Date.now(),
          }),
        )
      } catch {
        /* ignore quota / private mode */
      }
    } catch {
      try {
        const raw = localStorage.getItem(tenantBrandingCacheStorageKey())
        if (raw) {
          const parsed = JSON.parse(raw) as {
            branding?: TenantBrandingRecord
            brandingRevision?: number
            cpEntitlements?: unknown
          }
          if (parsed.branding && typeof parsed.branding === 'object' && !Array.isArray(parsed.branding)) {
            branding.value = parsed.branding
            const r = Number(parsed.brandingRevision)
            brandingRevision.value = Number.isFinite(r) ? r : 0
            const ce = parsed.cpEntitlements
            cpEntitlements.value = Array.isArray(ce) ? ce.map((x) => String(x)) : []
            applyBrandingToDocument(parsed.branding)
            return
          }
        }
      } catch {
        /* ignore corrupt cache */
      }
      branding.value = {}
      brandingRevision.value = 0
      cpEntitlements.value = []
    } finally {
      loaded.value = true
    }
  }

  async function load(): Promise<void> {
    if (loadInflight !== null) {
      return loadInflight
    }
    loadInflight = runLoad()
    try {
      await loadInflight
    } finally {
      loadInflight = null
    }
  }

  return {
    branding,
    brandingRevision,
    cpEntitlements,
    seatingProActive,
    giftCardsActive,
    vendorSystemActive,
    analyticsActive,
    loaded,
    load,
    pendingCpAddonCelebration,
    dismissCpAddonCelebration,
  }
})
